import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type StepperStep = {
  id: string
  label: string
  description?: string
  disabled?: boolean
  content: ReactNode
  renderFooter?: (context: StepperContextValue) => ReactNode
}

export type StepperContextValue = {
  steps: StepperStep[]
  activeStepId: string
  activeIndex: number
  goToStep: (stepId: string) => void
  goToNext: () => void
  goToPrevious: () => void
}

const StepperContext = createContext<StepperContextValue | null>(null)

export function useStepperContext(): StepperContextValue {
  const context = useContext(StepperContext)
  if (!context) {
    throw new Error('useStepperContext must be used within a Stepper')
  }
  return context
}

type StepperProps = {
  steps: Array<StepperStep | null | undefined | false>
  initialStepId?: string
  activeStepId?: string
  onStepChange?: (stepId: string) => void
  ariaLabel: string
  testIdPrefix?: string
  className?: string
  variant?: 'default' | 'workflow'
}

export function Stepper({
  steps,
  initialStepId,
  activeStepId,
  onStepChange,
  ariaLabel,
  testIdPrefix,
  className,
  variant = 'default',
}: StepperProps) {
  const normalizedSteps = useMemo(
    () => steps.filter((step): step is StepperStep => Boolean(step)),
    [steps]
  )

  const fallbackStepId = normalizedSteps[0]?.id ?? ''

  const resolvedInitialId = useMemo(() => {
    if (initialStepId) {
      const match = normalizedSteps.find(step => step.id === initialStepId)
      if (match) return match.id
    }
    return fallbackStepId
  }, [fallbackStepId, initialStepId, normalizedSteps])

  const isControlled = activeStepId !== undefined
  const [internalActive, setInternalActive] = useState(resolvedInitialId)

  useEffect(() => {
    if (isControlled) return
    setInternalActive(prev => {
      if (!prev) return resolvedInitialId
      const exists = normalizedSteps.some(step => step.id === prev)
      return exists ? prev : resolvedInitialId
    })
  }, [isControlled, normalizedSteps, resolvedInitialId])

  const currentActiveId = isControlled ? (activeStepId ?? '') : internalActive

  const setActiveStep = useCallback(
    (nextId: string) => {
      if (!nextId) return
      if (!isControlled) {
        setInternalActive(nextId)
      }
      if (nextId !== currentActiveId) {
        onStepChange?.(nextId)
      }
    },
    [currentActiveId, isControlled, onStepChange]
  )

  useEffect(() => {
    if (!isControlled) return
    if (!activeStepId) {
      if (fallbackStepId) {
        onStepChange?.(fallbackStepId)
      }
      return
    }
    const exists = normalizedSteps.some(step => step.id === activeStepId)
    if (!exists && fallbackStepId) {
      onStepChange?.(fallbackStepId)
    }
  }, [activeStepId, fallbackStepId, isControlled, normalizedSteps, onStepChange])

  const activeIndex = useMemo(() => {
    if (!currentActiveId) return 0
    const idx = normalizedSteps.findIndex(step => step.id === currentActiveId)
    return idx >= 0 ? idx : 0
  }, [currentActiveId, normalizedSteps])

  const activeStep = normalizedSteps[activeIndex]

  const goToIndex = useCallback(
    (index: number) => {
      const target = normalizedSteps[index]
      if (!target) return
      setActiveStep(target.id)
    },
    [normalizedSteps, setActiveStep]
  )

  const goToStep = useCallback(
    (stepId: string) => {
      const index = normalizedSteps.findIndex(step => step.id === stepId)
      if (index >= 0) {
        goToIndex(index)
      }
    },
    [goToIndex, normalizedSteps]
  )

  const goToNext = useCallback(() => {
    goToIndex(activeIndex + 1)
  }, [activeIndex, goToIndex])

  const goToPrevious = useCallback(() => {
    goToIndex(activeIndex - 1)
  }, [activeIndex, goToIndex])

  const contextValue = useMemo<StepperContextValue>(
    () => ({
      steps: normalizedSteps,
      activeStepId: activeStep?.id ?? '',
      activeIndex,
      goToStep,
      goToNext,
      goToPrevious,
    }),
    [activeStep?.id, activeIndex, goToNext, goToPrevious, goToStep, normalizedSteps]
  )

  if (!activeStep) {
    return null
  }

  const hasCustomFooter = typeof activeStep.renderFooter === 'function'
  const footerContent = hasCustomFooter ? activeStep.renderFooter?.(contextValue) : null

  return (
    <StepperContext.Provider value={contextValue}>
      <div className={cn('space-y-6', className)}>
        <ol
          className={
            variant === 'workflow'
              ? 'flex flex-wrap items-center gap-5'
              : 'flex flex-wrap items-center gap-2'
          }
          aria-label={ariaLabel}
        >
          {normalizedSteps.map((step, index) => {
            const isActive = index === activeIndex
            const isComplete = index < activeIndex
            return (
              <li key={step.id}>
                <button
                  type="button"
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isActive
                      ? 'border-primary bg-primary/10 text-primary'
                      : isComplete
                        ? 'border-muted-foreground/40 bg-muted text-muted-foreground'
                        : 'border-muted-foreground/40 bg-background text-muted-foreground hover:bg-muted',
                    variant === 'workflow' &&
                      'rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 pb-3 shadow-none',
                    variant === 'workflow' && isActive && 'border-primary bg-transparent'
                  )}
                  disabled={step.disabled}
                  aria-current={isActive ? 'step' : undefined}
                  onClick={() => goToIndex(index)}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold',
                      isActive
                        ? 'border-primary bg-primary text-primary-foreground'
                        : isComplete
                          ? 'border-muted-foreground/40 bg-muted-foreground text-background'
                          : 'border-muted-foreground/40 bg-background text-muted-foreground',
                      variant === 'workflow' &&
                        'h-auto w-auto rounded-none border-0 bg-transparent text-current'
                    )}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <span className="text-left">
                    <span className="block font-medium leading-tight">{step.label}</span>
                    {step.description && (
                      <span className="block text-xs text-muted-foreground">
                        {step.description}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>

        <div
          className={variant === 'workflow' ? 'min-w-0 space-y-6' : 'rounded-lg border bg-card p-4'}
          data-testid={
            testIdPrefix ? `${testIdPrefix}-${activeStep.id}` : `stepper-step-${activeStep.id}`
          }
          aria-live="polite"
        >
          {activeStep.content}
        </div>

        {hasCustomFooter ? (
          footerContent
        ) : (
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={goToPrevious}
              disabled={activeIndex === 0}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={goToNext}
              disabled={activeIndex >= normalizedSteps.length - 1}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </StepperContext.Provider>
  )
}
